import React, { memo } from "react";
import { Handle, Position } from "react-flow-renderer";
import { Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { ContentCopy, Delete, ArrowForwardIos } from "@mui/icons-material";
import { useNodeStorage } from "../../../stores/useNodeStorage";
import { MdBugReport } from "react-icons/md";

function DebugNode({ data, isConnectable, id }) {
  const theme = useTheme();
  const storageItems = useNodeStorage();

  const label = data?.label || "Debug";
  const variables = data?.variables || [];
  const logLevel = data?.logLevel || "info";

  const getLevelColor = (level) => {
    switch (level) {
      case "debug": return "#9e9e9e";
      case "info": return "#2196f3";
      case "warn": return "#ff9800";
      case "error": return "#f44336";
      default: return "#2196f3";
    }
  };

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        border: `2px solid ${getLevelColor(logLevel)}`,
        boxShadow: `0 4px 8px rgba(0, 0, 0, 0.15)`,
        position: "relative",
        transition: "all 0.2s ease",
        width: "auto",
        minWidth: "180px",
        maxWidth: "260px",
        boxSizing: "border-box",
      }}
    >
      {/* Handle de entrada */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          left: "-14px",
          top: "50%",
          transform: "translateY(-50%)",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, ${getLevelColor(logLevel)} 0%, ${getLevelColor(logLevel)}cc 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
                transform: "rotate(180deg)",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Botoes de acao */}
      <div
        style={{
          display: "flex",
          position: "absolute",
          right: theme.spacing(1),
          top: theme.spacing(1),
          gap: theme.spacing(1),
          cursor: "pointer",
        }}
      >
        <ContentCopy
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("duplicate");
          }}
          style={{
            width: "16px",
            height: "16px",
            color: theme.palette.primary.main,
          }}
        />
        <Delete
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("delete");
          }}
          style={{
            width: "16px",
            height: "16px",
            color: theme.palette.error.main,
          }}
        />
      </div>

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
          marginBottom: theme.spacing(1),
        }}
      >
        <MdBugReport
          style={{
            width: "18px",
            height: "18px",
            color: getLevelColor(logLevel),
          }}
        />
        <div
          style={{
            color: theme.palette.text.primary,
            fontSize: "15px",
            fontWeight: 500,
          }}
        >
          Debug
        </div>
      </div>

      {/* Label */}
      {label && (
        <Typography
          variant="body2"
          style={{
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(1),
            fontSize: "12px",
          }}
        >
          {label}
        </Typography>
      )}

      {/* Variáveis a logar */}
      {variables.length > 0 && (
        <div
          style={{
            backgroundColor: theme.palette.background.default,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(1),
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="caption"
            style={{
              color: theme.palette.text.secondary,
              display: "block",
              marginBottom: 4,
            }}
          >
            Variáveis:
          </Typography>
          {variables.slice(0, 3).map((v, i) => (
            <Typography
              key={i}
              variant="caption"
              style={{
                color: getLevelColor(logLevel),
                display: "block",
                fontFamily: "monospace",
                fontSize: "10px",
              }}
            >
              ${"{" + v + "}"}
            </Typography>
          ))}
          {variables.length > 3 && (
            <Typography
              variant="caption"
              style={{ color: theme.palette.text.secondary }}
            >
              +{variables.length - 3} mais...
            </Typography>
          )}
        </div>
      )}

      {/* Handle de saída */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          right: "-14px",
          top: "50%",
          transform: "translateY(-50%)",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, ${getLevelColor(logLevel)} 0%, ${getLevelColor(logLevel)}cc 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>
    </div>
  );
}

export default memo(DebugNode);
